import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { LlmPdCost, LlmPdListRow, LlmUsageApiService } from 'src/app/core/services/llm-usage-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ACTION_BUTTON } from 'src/app/shared/utils/action-button.classes';
import { formatUsd } from '../llm-cost/llm-cost.filters';
import { downloadCsv, toCsv } from '../llm-cost/csv-export.util';
import { CallDetailDrawerComponent } from '../llm-cost/call-detail-drawer/call-detail-drawer.component';

/**
 * What one PD score actually cost in AI.
 *
 * Shows the calls and their tokens only. Prompts, model responses and customer fields are
 * deliberately not available here — this is a KYC platform, and a cost screen is not a reason
 * to surface what was sent to a model.
 */
@Component({
  selector: 'app-llm-pd-cost',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PageSurfaceHeaderComponent, CallDetailDrawerComponent],
  templateUrl: './llm-pd-cost.component.html',
})
export class LlmPdCostComponent implements OnInit {
  readonly btn = ACTION_BUTTON;
  private readonly api = inject(LlmUsageApiService);
  private readonly route = inject(ActivatedRoute);

  readonly formatUsd = formatUsd;

  sessionCode = '';
  result: LlmPdCost | null = null;
  loading = false;
  error = '';
  searched = false;

  /** The browse list: which PDs cost the most, before you know which one to look at. */
  pdRows: LlmPdListRow[] = [];
  pdMeta = { total: 0, page: 1, limit: 25, pages: 0 };
  listLoading = false;

  /** Call id whose drawer is open. */
  openCallId: number | null = null;

  ngOnInit(): void {
    const fromPdId = this.route.snapshot.queryParamMap.get('pd_id');
    const fromQuery = this.route.snapshot.queryParamMap.get('session_code');
    if (fromPdId) {
      this.sessionCode = fromPdId;
      this.search();
    } else if (fromQuery) {
      this.sessionCode = fromQuery;
      this.search();
    }
    this.loadList();
  }

  search(): void {
    const code = this.sessionCode.trim();
    if (!code) return;

    this.loading = true;
    this.error = '';
    this.searched = true;
    this.api
      .pdCost(code)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => (this.result = res.data ?? null),
        error: (err) => {
          this.result = null;
          // A 404 means no record exists, which is NOT the same as a PD that cost nothing.
          this.error = resolveApiErrorMessage(err) || `No LLM usage recorded for ${code}.`;
        },
      });
  }

  loadList(page = 1): void {
    this.listLoading = true;
    this.api
      .pdList({ page, limit: this.pdMeta.limit })
      .pipe(finalize(() => (this.listLoading = false)))
      .subscribe({
        next: (res) => {
          this.pdRows = res.data ?? [];
          this.pdMeta = res.meta ?? this.pdMeta;
        },
        error: () => (this.pdRows = []),
      });
  }

  openPd(sessionCode: string): void {
    this.sessionCode = sessionCode;
    this.search();
  }

  openCall(id: number): void {
    this.openCallId = id;
  }

  closeCall(): void {
    this.openCallId = null;
  }

  statusClass(status: string): string {
    if (status === 'SUCCESS') return 'badge bg-success-light text-success';
    if (status === 'FAILED') return 'badge bg-danger-light text-danger';
    return 'badge bg-warning-light text-warning';
  }

  exportCsv(): void {
    if (!this.result) return;
    const headers = [
      { key: 'request_timestamp', label: 'Date/Time' },
      { key: 'task_code', label: 'Task' },
      { key: 'model_name', label: 'Model' },
      { key: 'input_tokens', label: 'Input Tokens' },
      { key: 'output_tokens', label: 'Output Tokens' },
      { key: 'total_tokens', label: 'Total Tokens' },
      { key: 'total_cost_usd', label: 'Cost (USD)' },
      { key: 'status', label: 'Status' },
    ];
    downloadCsv(`llm-pd-${this.result.pd.session_code}.csv`, toCsv(headers, this.result.calls as unknown as Record<string, unknown>[]));
  }
}
